"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import { WaveDropsAllWithoutProvider } from "@/components/waves/drops/wave-drops-all";
import { WaveGallery } from "@/components/waves/gallery";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMarkWaveNotificationsRead } from "@/hooks/useMarkWaveNotificationsRead";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { ACCEPTED_FILE_TYPE_LABELS, isSupportedUploadFile } from "@/services/uploads/mediaUploadMimeType";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
import { useWaveChatLeaveCleanup } from "./useWaveChatLeaveCleanup";
import { WaveChatSubmitDropModal } from "./WaveChatSubmitDropModal";
import type { ChatSubmitDropAction, ChatSubmitDropState } from "./chatSubmitDrop.types";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
  readonly viewMode: WaveViewMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly chatSubmitDrop?: ChatSubmitDropState | null | undefined;
  readonly chatSubmitDropAction?: ChatSubmitDropAction | null | undefined;
  readonly onCloseChatSubmitDrop?: (() => void) | undefined;
}
interface WaveChatLeaveHandlerProps {
  readonly enabled: boolean;
  readonly waveId: string;
}
const MAX_UNSUPPORTED_FILE_NAMES_IN_TOAST = 3;
const noop = () => {};
const WaveChatLeaveHandler: React.FC<WaveChatLeaveHandlerProps> = t0 => {
  const $ = _c(6);
  const {
    enabled,
    waveId
  } = t0;
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const markWaveNotificationsRead = useMarkWaveNotificationsRead();
  let t1;
  if ($[0] !== enabled || $[1] !== markWaveNotificationsRead || $[2] !== removeWaveDeliveredNotifications || $[3] !== setUnreadDividerSerialNo || $[4] !== waveId) {
    t1 = {
      enabled,
      waveId,
      setUnreadDividerSerialNo,
      removeWaveDeliveredNotifications,
      markWaveNotificationsRead
    };
    $[0] = enabled;
    $[1] = markWaveNotificationsRead;
    $[2] = removeWaveDeliveredNotifications;
    $[3] = setUnreadDividerSerialNo;
    $[4] = waveId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useWaveChatLeaveCleanup(t1);
  return null;
};
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(101);
  const {
    wave,
    firstUnreadSerialNo,
    viewMode,
    onDropClick,
    chatSubmitDrop: t1,
    chatSubmitDropAction: t2,
    onCloseChatSubmitDrop
  } = t0;
  const chatSubmitDrop = t1 === undefined ? null : t1;
  const chatSubmitDropAction = t2 === undefined ? null : t2;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const galleryContainerRef = useRef(null);
  const dragCounterRef = useRef(0);
  const externalAttachmentDropTokenRef = useRef(0);
  const {
    connectedProfile,
    setToast
  } = useAuth();
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const [isDragDropActive, setIsDragDropActive] = useState(false);
  const [externalAttachmentDrop, setExternalAttachmentDrop] = useState(null);
  let t3;
  if ($[0] !== wave.id) {
    t3 = {
      waveId: wave.id,
      activeDrop: null
    };
    $[0] = wave.id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [activeDropState, setActiveDropState] = useState(t3);
  const activeDrop = activeDropState.waveId === wave.id ? activeDropState.activeDrop : null;
  let t4;
  if ($[2] !== wave.id) {
    t4 = nextActiveDrop => {
      setActiveDropState({
        waveId: wave.id,
        activeDrop: nextActiveDrop
      });
    };
    $[2] = wave.id;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const setActiveDropForWave = t4;
  let t5;
  if ($[4] !== firstUnreadSerialNo || $[5] !== searchParams || $[6] !== wave.id) {
    bb0: {
      const dropParam = searchParams.get("serialNo");
      if (!dropParam) {
        t5 = null;
        break bb0;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[8] !== firstUnreadSerialNo || $[9] !== searchParams) {
        const dividerParam = searchParams.get("divider");
        const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
        t6 = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
        $[8] = firstUnreadSerialNo;
        $[9] = searchParams;
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      const dividerSerialNo = t6;
      let t7;
      if ($[11] !== dividerSerialNo || $[12] !== parsed || $[13] !== wave.id) {
        t7 = {
          waveId: wave.id,
          serialNo: parsed,
          dividerSerialNo
        };
        $[11] = dividerSerialNo;
        $[12] = parsed;
        $[13] = wave.id;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      t5 = t7;
    }
    $[4] = firstUnreadSerialNo;
    $[5] = searchParams;
    $[6] = wave.id;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const initialDropState = t5;
  let t6;
  if ($[15] !== firstUnreadSerialNo || $[16] !== initialDropState || $[17] !== wave.id) {
    t6 = () => {
      if (initialDropState?.waveId === wave.id && initialDropState.dividerSerialNo !== null) {
        return {
          waveId: wave.id,
          serialNo: initialDropState.dividerSerialNo
        };
      }
      return {
        waveId: wave.id,
        serialNo: firstUnreadSerialNo
      };
    };
    $[15] = firstUnreadSerialNo;
    $[16] = initialDropState;
    $[17] = wave.id;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const [capturedDividerState] = useState(t6);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  let dividerTarget = firstUnreadSerialNo;
  if (initialDropState?.waveId === wave.id) {
    dividerTarget = initialDropState.dividerSerialNo;
  } else {
    if (capturedDividerState.waveId === wave.id) {
      dividerTarget = capturedDividerState.serialNo;
    }
  }
  let t7;
  let t8;
  if ($[19] !== initialDropState || $[20] !== pathname || $[21] !== router || $[22] !== searchParams || $[23] !== viewMode) {
    t7 = () => {
      if (!initialDropState || viewMode === "gallery") {
        return;
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      if (!params.has("serialNo") && !params.has("divider")) {
        return;
      }
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t8 = [initialDropState, pathname, router, searchParams, viewMode];
    $[19] = initialDropState;
    $[20] = pathname;
    $[21] = router;
    $[22] = searchParams;
    $[23] = viewMode;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  useEffect(t7, t8);
  const {
    waveViewStyle
  } = useLayout();
  let t9;
  if ($[26] !== setActiveDropForWave) {
    const onReply = (drop, partId) => {
      setActiveDropForWave({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    t9 = t10 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t10;
      onReply(drop_0, partId_0);
    };
    $[26] = setActiveDropForWave;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const handleReply = t9;
  let t10;
  if ($[28] !== setActiveDropForWave) {
    t10 = () => {
      setActiveDropForWave(null);
    };
    $[28] = setActiveDropForWave;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onCancelReplyQuote = t10;
  let t11;
  if ($[30] !== wave) {
    t11 = {
      wave
    };
    $[30] = wave;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const {
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t11);
  const fixedDropMode = DropMode.CHAT;
  const activeChatSubmitDropExperience = chatSubmitDrop?.submissionExperience ?? null;
  const showFixedParticipationSubmit = activeChatSubmitDropExperience !== null && activeChatSubmitDropExperience !== WaveSubmissionExperience.CURATION_LEGACY && activeChatSubmitDropExperience !== WaveSubmissionExperience.MEMES_LEGACY && activeChatSubmitDropExperience !== WaveSubmissionExperience.QUORUM_PROPOSAL;
  const showQuorumProposalSubmit = activeChatSubmitDropExperience === WaveSubmissionExperience.QUORUM_PROPOSAL;
  const showCurationSubmitModal = activeChatSubmitDropExperience === WaveSubmissionExperience.CURATION_LEGACY;
  const closeChatSubmitDrop = onCloseChatSubmitDrop ?? noop;
  const chatCurationUrlSubmitHandler = chatSubmitDropAction?.isVisible === true ? chatSubmitDropAction.onOpenWithCurationUrl : undefined;
  const canSubmitChatCurationUrl = chatSubmitDropAction?.isVisible === true && chatSubmitDropAction.canOpen;
  const chatCurationUrlSubmitRestrictionMessage = chatSubmitDropAction?.isVisible === true ? chatSubmitDropAction.restrictionMessage : null;
  const isChatSubmitFlowOpen = activeChatSubmitDropExperience !== null;
  const shouldHandleContainerFileDrop = _temp;
  const isFileDragEvent = _temp2;
  const filterSupportedFiles = _temp3;
  let t12;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      dragCounterRef.current = 0;
      setIsDragDropActive(false);
    };
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const resetDragDropState = t12;
  let t13;
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      globalThis.window.addEventListener("dragend", resetDragDropState);
      globalThis.window.addEventListener("drop", resetDragDropState);
      return () => {
        globalThis.window.removeEventListener("dragend", resetDragDropState);
        globalThis.window.removeEventListener("drop", resetDragDropState);
      };
    };
    t14 = [resetDragDropState];
    $[33] = t13;
    $[34] = t14;
  } else {
    t13 = $[33];
    t14 = $[34];
  }
  useEffect(t13, t14);
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = event_1 => {
      if (!isFileDragEvent(event_1) || !shouldHandleContainerFileDrop(event_1)) {
        return;
      }
      event_1.preventDefault();
      event_1.stopPropagation();
      dragCounterRef.current = dragCounterRef.current + 1;
      setIsDragDropActive(true);
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const onContainerDragEnter = t15;
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = event_2 => {
      if (!isFileDragEvent(event_2) || !shouldHandleContainerFileDrop(event_2)) {
        return;
      }
      event_2.preventDefault();
      event_2.stopPropagation();
      event_2.dataTransfer.dropEffect = "copy";
    };
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const onContainerDragOver = t16;
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_3 => {
      if (!isFileDragEvent(event_3) || !shouldHandleContainerFileDrop(event_3)) {
        return;
      }
      event_3.preventDefault();
      event_3.stopPropagation();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragDropActive(false);
      }
    };
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const onContainerDragLeave = t17;
  let onContainerDrop;
  if ($[38] !== setToast) {
    onContainerDrop = event_4 => {
      if (!isFileDragEvent(event_4) || !shouldHandleContainerFileDrop(event_4)) {
        return;
      }
      event_4.preventDefault();
      event_4.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragDropActive(false);
      const droppedFiles = Array.from(event_4.dataTransfer.files);
      if (droppedFiles.length === 0) {
        return;
      }
      handleExternalAttachmentFiles(droppedFiles);
    };
    const formatUnsupportedFileNames = _temp5;
    const handleExternalAttachmentFiles = files_0 => {
      if (files_0.length === 0) {
        return;
      }
      const {
        supported: supported_0,
        unsupported: unsupported_1
      } = filterSupportedFiles(files_0);
      if (supported_0.length > 0) {
        externalAttachmentDropTokenRef.current = externalAttachmentDropTokenRef.current + 1;
        setExternalAttachmentDrop({
          token: externalAttachmentDropTokenRef.current,
          files: supported_0
        });
      }
      if (unsupported_1.length > 0) {
        const unsupportedNames = formatUnsupportedFileNames(unsupported_1);
        setToast({
          message: `Unsupported file type: ${unsupportedNames}. Accepted Types: ${ACCEPTED_FILE_TYPE_LABELS}`,
          type: "error"
        });
      }
    };
    $[38] = setToast;
    $[39] = onContainerDrop;
  } else {
    onContainerDrop = $[39];
  }
  if (viewMode === "gallery") {
    let t18;
    if ($[40] !== onDropClick || $[41] !== wave) {
      t18 = <WaveGallery wave={wave} onDropClick={onDropClick} />;
      $[40] = onDropClick;
      $[41] = wave;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] !== t18 || $[44] !== waveViewStyle) {
      t19 = <div ref={galleryContainerRef} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden" style={waveViewStyle}>{t18}</div>;
      $[43] = t18;
      $[44] = waveViewStyle;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    return t19;
  }
  const t18 = dividerTarget ?? null;
  const t19 = `unread-divider-${wave.id}`;
  const t20 = Boolean(connectedProfile?.handle);
  let t21;
  if ($[46] !== t20 || $[47] !== wave.id) {
    t21 = <WaveChatLeaveHandler enabled={t20} waveId={wave.id} />;
    $[46] = t20;
    $[47] = wave.id;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== isDragDropActive) {
    t22 = isDragDropActive && <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-40 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dotted tw-border-primary-400 tw-bg-iron-900/75 tw-p-6"><div className="tw-max-w-3xl tw-p-4 tw-text-center"><p className="tw-text-base tw-font-semibold tw-text-primary-300">Drop files here</p><p className="tw-mt-2 tw-text-xs tw-text-iron-300">Accepted types: {ACCEPTED_FILE_TYPE_LABELS}</p></div></div>;
    $[49] = isDragDropActive;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== activeDrop || $[52] !== dividerTarget || $[53] !== handleReply || $[54] !== isVotingClosed || $[55] !== isVotingControlsLocked || $[56] !== onDropClick || $[57] !== scrollTarget || $[58] !== wave || $[59] !== winningThreshold || $[60] !== winningThresholdMinDurationMs) {
    t23 = <WaveDropsAllWithoutProvider key={wave.id} waveId={wave.id} wave={wave} onReply={handleReply} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} unreadCount={wave.metrics.your_unread_drops_count} dropId={null} onDropContentClick={onDropClick} isMuted={wave.metrics.muted} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[51] = activeDrop;
    $[52] = dividerTarget;
    $[53] = handleReply;
    $[54] = isVotingClosed;
    $[55] = isVotingControlsLocked;
    $[56] = onDropClick;
    $[57] = scrollTarget;
    $[58] = wave;
    $[59] = winningThreshold;
    $[60] = winningThresholdMinDurationMs;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== activeDrop || $[63] !== canSubmitChatCurationUrl || $[64] !== chatCurationUrlSubmitHandler || $[65] !== chatCurationUrlSubmitRestrictionMessage || $[66] !== editingDropId || $[67] !== externalAttachmentDrop || $[68] !== isApp || $[69] !== isChatSubmitFlowOpen || $[70] !== onCancelReplyQuote || $[71] !== wave) {
    t24 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={fixedDropMode} externalAttachmentDrop={externalAttachmentDrop} onExternalAttachmentDropConsumed={() => setExternalAttachmentDrop(null)} onSubmitCurationUrl={chatCurationUrlSubmitHandler} canSubmitCurationUrl={canSubmitChatCurationUrl} curationUrlSubmitRestrictionMessage={chatCurationUrlSubmitRestrictionMessage} termsSignatureFlowEnabled={!isChatSubmitFlowOpen} /></CreateDropWaveWrapper></div>;
    $[62] = activeDrop;
    $[63] = canSubmitChatCurationUrl;
    $[64] = chatCurationUrlSubmitHandler;
    $[65] = chatCurationUrlSubmitRestrictionMessage;
    $[66] = editingDropId;
    $[67] = externalAttachmentDrop;
    $[68] = isApp;
    $[69] = isChatSubmitFlowOpen;
    $[70] = onCancelReplyQuote;
    $[71] = wave;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== chatSubmitDrop?.initialCurationUrl || $[74] !== closeChatSubmitDrop || $[75] !== showFixedParticipationSubmit || $[76] !== wave) {
    t25 = showFixedParticipationSubmit && <WaveChatSubmitDropModal isOpen={true} wave={wave} title="Submit drop" initialCurationUrl={chatSubmitDrop?.initialCurationUrl ?? null} onClose={closeChatSubmitDrop} />;
    $[73] = chatSubmitDrop?.initialCurationUrl;
    $[74] = closeChatSubmitDrop;
    $[75] = showFixedParticipationSubmit;
    $[76] = wave;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== closeChatSubmitDrop || $[79] !== showQuorumProposalSubmit || $[80] !== wave) {
    t26 = showQuorumProposalSubmit && <WaveDropCreate wave={wave} onCancel={closeChatSubmitDrop} onSuccess={closeChatSubmitDrop} onExitFixedDropMode={closeChatSubmitDrop} isModalContent={true} />;
    $[78] = closeChatSubmitDrop;
    $[79] = showQuorumProposalSubmit;
    $[80] = wave;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== chatSubmitDrop?.initialCurationUrl || $[83] !== closeChatSubmitDrop || $[84] !== showCurationSubmitModal || $[85] !== wave) {
    t27 = showCurationSubmitModal && <WaveLeaderboardCurationDropModal isOpen={true} wave={wave} initialUrl={chatSubmitDrop?.initialCurationUrl ?? null} onClose={closeChatSubmitDrop} />;
    $[82] = chatSubmitDrop?.initialCurationUrl;
    $[83] = closeChatSubmitDrop;
    $[84] = showCurationSubmitModal;
    $[85] = wave;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] !== onContainerDrop || $[88] !== t22 || $[89] !== t23 || $[90] !== t24 || $[91] !== t25 || $[92] !== t26 || $[93] !== t27 || $[94] !== waveViewStyle) {
    t28 = <section className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow tw-relative" style={waveViewStyle} aria-label="Wave chat file upload area" onDragEnter={onContainerDragEnter} onDragOver={onContainerDragOver} onDragLeave={onContainerDragLeave} onDrop={onContainerDrop}>{t22}{t23}{t24}{t25}{t26}{t27}</section>;
    $[87] = onContainerDrop;
    $[88] = t22;
    $[89] = t23;
    $[90] = t24;
    $[91] = t25;
    $[92] = t26;
    $[93] = t27;
    $[94] = waveViewStyle;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  let t29;
  if ($[96] !== t18 || $[97] !== t19 || $[98] !== t21 || $[99] !== t28) {
    t29 = <UnreadDividerProvider initialSerialNo={t18} key={t19}>{t21}{t28}</UnreadDividerProvider>;
    $[96] = t18;
    $[97] = t19;
    $[98] = t21;
    $[99] = t28;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  return t29;
};
export default MyStreamWaveChat;
function _temp(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return true;
  }
  return target.closest("[contenteditable=\"true\"], input, textarea, [role=\"textbox\"]") === null;
}
function _temp2(event_0) {
  return Array.from(event_0.dataTransfer.types).includes("Files");
}
function _temp3(files) {
  const supported = [];
  const unsupported = [];
  files.forEach(file => {
    if (isSupportedUploadFile(file)) {
      supported.push(file);
    } else {
      unsupported.push(file);
    }
  });
  return {
    supported,
    unsupported
  };
}
function _temp4(file_0) {
  return file_0.name;
}
function _temp5(unsupported_0) {
  const displayedNames = unsupported_0.slice(0, MAX_UNSUPPORTED_FILE_NAMES_IN_TOAST).map(_temp4);
  const remainingCount = Math.max(0, unsupported_0.length - displayedNames.length);
  return remainingCount > 0 ? `${displayedNames.join(", ")} and ${remainingCount} more` : displayedNames.join(", ");
}